import { Injectable } from '@angular/core';
import { DeadlineDateCalcualator } from '@zaoblako/shared/common/classes/deadline.date.calcualator';
import { TemplateEntityVariableEngine } from '@zaoblako/shared/common/classes/template.entity.variable.engine';
import type { PaginationRequiredQueryInterface } from '@zaoblako/shared/common/dtos/pagination-required-query.dto';
import type { ReportDescriptorInterface } from '@zaoblako/shared/common/dtos/report/report-descriptor.dto';
import type { TaskFormInterface } from '@zaoblako/shared/modules/task/dtos/task-form.dto';
import type { TaskTemplateFindInterface } from '@zaoblako/shared/modules/task/dtos/template/task-template-find.dto';
import type { TaskTemplateHistoryPageInterface } from '@zaoblako/shared/modules/task/dtos/template/task.template.history.page.dto';
import type { TaskTemplateFlatListQueryInterface } from '@zaoblako/shared/modules/task/dtos/template/task-template-flat-list-query.dto';
import type { TaskTemplateRegistryQueryInterface } from '@zaoblako/shared/modules/task/dtos/template/task-template-registry-query.dto';
import type { TaskTemplatePageInterface } from '@zaoblako/shared/modules/task/dtos/template/task-template-page.dto';

import { map, Observable } from 'rxjs';
import { BaseService } from '../base.service';

@Injectable({ providedIn: 'root' })
export class TaskTemplateService extends BaseService {
  protected baseUrl = '/finance/brs/task/template';

  public getRegistryDescriptor(): Observable<ReportDescriptorInterface> {
    return this.http.get<ReportDescriptorInterface>(`${this.baseUrl}/`);
  }

  public findPage(queryParams: TaskTemplateRegistryQueryInterface): Observable<TaskTemplatePageInterface> {
    return this.http.get<TaskTemplatePageInterface>(`${this.baseUrl}/items`, {
      params: this.objectToHttpParams(queryParams as Record<string, unknown>)
    });
  }

  public findManyFlat(params?: TaskTemplateFlatListQueryInterface): Observable<TaskTemplateFindInterface[]> {
    return this.http.get<TaskTemplateFindInterface[]>(`${this.baseUrl}/flat`, {
      params: params ? this.objectToHttpParams(params as Record<string, unknown>) : {}
    });
  }

  public getTaskFormByTemplate(id: number): Observable<{ task: TaskFormInterface }> {
    return this.findById<TaskTemplateFindInterface>(id).pipe(
      map((template) => {
        const task = <TaskFormInterface>{};
        const fieldsToCopy: Array<keyof TaskTemplateFindInterface> = [
          'kind',
          'status',
          'creditPayment',
          'contragent',
          'contragentBankDetails',
          'responsible',
          'organization',
          'filial',
          'unit',
          'direction',
          'type',
          'priority',
          'amount',
          'nds',
          'userAuditors',
          'userGroupAuditors',
          'organizationBankDetails',
          'payerStatus',
          'paymentId',
          'paymentReason',
          'budgetClassificactionCode',
          'taxPeriod',
          'municipalityClassificationCode',
          'uniqueAccrualIdentifier',
          'documentNumber'
        ];

        fieldsToCopy.forEach((field) => (task[field] = template[field]));
        console.debug('template.deadline', template.deadline);
        console.debug('template.documentDate', template.documentDate);
        console.debug('template.planning.nearestDate', template.planning.nearestDate);
        console.debug('template.planning.runDate', template.planning.runDate);
        task.date = DeadlineDateCalcualator.getInstance().calculateDate(
          template.deadline,
          template.planning.nearestDate || template.planning.runDate
        );
        task.documentDate = DeadlineDateCalcualator.getInstance().calculateDate(
          template.documentDate,
          template.planning.nearestDate || template.planning.runDate
        );
        task.orders = [
          {
            amount: template.amount,
            subject: TemplateEntityVariableEngine.getInstance().parse(template.subject),
            date: task.date
          }
        ];
        task.scheme = template.scheme;
        task.subject = TemplateEntityVariableEngine.getInstance().parse(template.subject);

        if (template.currency) {
          task.currency = template.currency;
          task['rate'] = template.currency?.latestRate?.rate || 1;
        }

        return { task };
      })
    );
  }

  public historyById(
    templateId: number,
    queryParams: PaginationRequiredQueryInterface
  ): Observable<TaskTemplateHistoryPageInterface> {
    return this.http.get<TaskTemplateHistoryPageInterface>(`${this.baseUrl}/${templateId}/history`, {
      params: this.objectToHttpParams(queryParams)
    });
  }
}
